import {COMPONENT_HTML_SUFFIX} from '@features/components/compiler/constants';

/**
 * Prefix used when rewriting component screen IDs for the LLM context.
 * e.g. "flowId/screens/Home.jsx" → "@screen Home.jsx"
 */
export const SCREEN_ID_PREFIX = '@screen ';
export const COMPONENT_ID_PREFIX = '@component ';

/**
 * Component screen IDs follow the pattern: {flowId}/screens/{filename}.jsx
 */
export function isComponentScreen(screenId: string): boolean {
  return screenId.split('/').at(1) === 'screens';
}

/**
 * Component library item IDs follow the pattern: {flowId}/components/{filename}.jsx
 */
export function isComponentLibraryItem(id: string): boolean {
  return id.split('/').at(1) === 'components';
}

/**
 * Returns true for any component-managed ID (screen or library item).
 */
export function isComponentId(id: string): boolean {
  return isComponentScreen(id) || isComponentLibraryItem(id);
}

/**
 * Shared parser for component-managed IDs (both screens and library items).
 */
export function parseId(id: string) {
  const parts = id.split('/');
  const kind = parts.at(1) ?? 'screens';
  const filename = parts.at(-1) ?? '';
  const suffix = kind === 'components' ? COMPONENT_HTML_SUFFIX : '.html';
  return {
    flowId: parts[0] ?? '',
    filename,
    fsPath: `/${kind}/${filename}`,
    outPath: `/${filename.replace('.jsx', suffix)}`,
  };
}

export function parseComponentScreenId(screenId: string) {
  return parseId(screenId);
}

export function parseComponentId(id: string) {
  return parseId(id);
}

export const stripJsx = (s: string) => s.replace(/\.jsx$/, '');
const ensureJsx = (s: string) => (s.endsWith('.jsx') ? s : `${s}.jsx`);

/**
 * URL keys used to encode the active screen/component selection. Components
 * screens and library items use short typed keys; HTML/CSS legacy flows keep
 * `activeScreenId`. Order is read-priority.
 */
export const SHARE_KEYS = ['screen', 'component', 'activeScreenId'] as const;
type ShareKey = (typeof SHARE_KEYS)[number];

/**
 * Encode an internal screen id into the `(key, value)` pair to put on the URL.
 */
export function encodeShareParam(id: string): {key: ShareKey; value: string} {
  if (isComponentScreen(id)) {
    return {
      key: 'screen',
      value: stripJsx(parseComponentScreenId(id).filename),
    };
  }
  if (isComponentLibraryItem(id)) {
    return {
      key: 'component',
      value: stripJsx(parseComponentId(id).filename),
    };
  }
  return {key: 'activeScreenId', value: id};
}

/**
 * Decode the URL keys back into an internal screen id. Component shorts need
 * `flowId` from the route to expand to `{flowId}/screens/{name}.jsx` etc.
 * Returns `null` when no key is set or when a component short is missing flowId.
 */
export function decodeShareParam(
  raw: Record<ShareKey, string | null>,
  flowId: string | undefined,
): string | null {
  if (raw.screen && flowId) return `${flowId}/screens/${ensureJsx(raw.screen)}`;
  if (raw.component && flowId) {
    return `${flowId}/components/${ensureJsx(raw.component)}`;
  }
  return raw.activeScreenId;
}
